import { TestCaseDesignElement } from "@design-controls/types";
import {
  DataProvider,
  QDataTable,
  QIcon,
  QStack,
  QTag,
  QText,
} from "@qualio/ui-components";
import React, { useMemo } from "react";
import { sanitize } from "dompurify";
import diff from "../htmldiff/htmldiff";
import {
  ResultValueToLabel,
  TestResultVariantColour,
} from "../../../constants";
import { useTranslation } from "react-i18next";

type Props = {
  base: TestCaseDesignElement;
  compare: TestCaseDesignElement;
};

type Column = {
  label: string;
  base: any;
  compare: any;
};

export const TestCase: React.FC<Props> = ({ base, compare }) => {
  const { t } = useTranslation();
  const comparison = diff(base?.description ?? "", compare?.description ?? "");

  const rows = useMemo(() => {
    const rows: Column[] = [];

    if (base.title !== compare.title) {
      rows.push({
        label: t("diffView.fields.title"),
        base: base.title,
        compare: compare.title,
      });
    }

    if (base.category?.qri !== base.category?.qri) {
      rows.push({
        label: t("diffView.fields.category"),
        base: base.category?.label,
        compare: compare.category?.label,
      });
    }

    if (base.testResult?.executedBy !== compare.testResult?.executedBy) {
      rows.push({
        label: t("diffView.fields.tester"),
        base: base.testResult?.executedBy,
        compare: compare.testResult?.executedBy,
      });
    }

    if (base.testResult?.date !== compare.testResult?.date) {
      rows.push({
        label: t("diffView.fields.executedDated"),
        base: base.testResult?.date,
        compare: compare.testResult?.date,
      });
    }

    if (base.testResult?.notes !== compare.testResult?.notes) {
      rows.push({
        label: t("diffView.fields.notes"),
        base: base.testResult?.notes,
        compare: compare.testResult?.notes,
      });
    }

    if (base.testResult?.result !== compare.testResult?.result) {
      rows.push({
        label: t("diffView.fields.result"),
        base: base.testResult && (
          <QTag variantColor={TestResultVariantColour[base.testResult.result]}>
            {ResultValueToLabel[base.testResult.result]}
          </QTag>
        ),
        compare: compare.testResult && (
          <QTag
            variantColor={TestResultVariantColour[compare.testResult.result]}
          >
            {ResultValueToLabel[compare.testResult.result]}
          </QTag>
        ),
      });
    }

    return rows;
  }, [base, compare, t]);

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "label",
        header: t("diffView.table.header.label"),
        meta: {
          width: "200px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) => (
          <QText fontWeight={600}>{row.label}</QText>
        ),
      },
      {
        accessorKey: "base",
        header: t("diffView.table.header.base"),
        meta: {
          minWidth: "200px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) =>
          row.base ?? (
            <QText color="gray.500">{t("diffView.table.empty")}</QText>
          ),
      },
      {
        accessorKey: "id",
        header: t("diffView.table.header.icon"),
        meta: {
          width: "60px",
        },
        cell: () => <QIcon iconName="ArrowRight" />,
      },
      {
        accessorKey: "compare",
        header: t("diffView.table.header.compare"),
        meta: {
          minWidth: "200px",
        },
        cell: ({ row: { original: row } }: { row: { original: Column } }) =>
          row.compare ?? (
            <QText color="gray.500">{t("diffView.table.empty")}</QText>
          ),
      },
    ];
  }, [t]);

  return (
    <QStack gap="16px">
      {rows.length && (
        <DataProvider.Fixed data={rows}>
          <QDataTable columns={columns} hideItemCount withHeaders={false} />
        </DataProvider.Fixed>
      )}
      {base.description !== compare.description && (
        <QStack>
          <QText fontSize="sm" fontWeight={600} color="gray.900">
            {t("diffView.fields.description")}
          </QText>

          <QText
            fontSize={14}
            style={{ wordBreak: "break-word" }}
            dangerouslySetInnerHTML={{
              __html: sanitize(comparison),
            }}
          />
        </QStack>
      )}
    </QStack>
  );
};
